/**
 * Deep comparison between any two values.
 * Supports primitives, arrays, plain objects, Sets, Maps, and Dates.
 */
export function deepEqual<T>(a: T, b: T, ignoreOrder = false): boolean {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return false;

  // Compare Date
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Compare Set
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const valA of a) {
      let hasMatch = false;
      for (const valB of b) {
        if (deepEqual(valA, valB, ignoreOrder)) {
          hasMatch = true;
          break;
        }
      }
      if (!hasMatch) return false;
    }
    return true;
  }

  // Compare Map
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, valA] of a.entries()) {
      if (!b.has(key)) return false;
      const valB = b.get(key);
      if (!deepEqual(valA, valB, ignoreOrder)) return false;
    }
    return true;
  }

  // Compare Array
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const arrA = ignoreOrder ? [...a].sort() : a;
    const arrB = ignoreOrder ? [...b].sort() : b;
    return arrA.every((val, i) => deepEqual(val, arrB[i], ignoreOrder));
  }

  // Compare Object
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) =>
      deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], ignoreOrder)
    );
  }

  return false;
}

/**
 * Generic array equality checker.
 * @param arr1 First array
 * @param arr2 Second array
 * @param options Optional comparator or ignoreOrder flag
 */
export function areArraysEqual<T>(
  arr1: T[],
  arr2: T[],
  options?: {
    comparator?: (a: T, b: T) => boolean;
    ignoreOrder?: boolean;
  }
): boolean {
  const { comparator, ignoreOrder = true } = options || {};
  if (arr1.length !== arr2.length) return false;

  const a = ignoreOrder ? [...arr1].sort() : arr1;
  const b = ignoreOrder ? [...arr2].sort() : arr2;

  const compare: (x: T, y: T) => boolean = comparator ?? ((x, y) => deepEqual(x, y, ignoreOrder));

  return a.every((val, i) => compare(val, b[i]));
}
